import React, { useState, useRef, useEffect } from 'react';
import { Camera } from 'lucide-react';

const PhotoCapture = ({ onCapture, label, required = true }) => {
  const [captured, setCaptured] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const startCamera = async () => {
    if (isStarting || streamRef.current) return;

    setIsStarting(true);
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;

        await new Promise((resolve) => {
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play()
              .then(resolve)
              .catch((e) => {
                console.warn('Play failed:', e);
                resolve();
              });
          };
        });
        setIsReady(true);
      }
    } catch (err) {
      console.error('Camera error:', err);
      setError(
        err.name === 'NotAllowedError'
          ? 'Camera permission denied. Please allow access in browser settings.'
          : 'Camera not available. Try HTTPS or check device camera.'
      );
    } finally {
      setIsStarting(false);
    }
  };

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current || !videoRef.current.srcObject) {
      setError('Camera not ready. Try again.');
      return;
    }

    const video = videoRef.current;
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      setError('Camera preview not loaded. Try recapture.');
      return;
    }

    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const photoData = canvas.toDataURL('image/jpeg', 0.92);

    const photo = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      location: 'GPS: -24.6282, 25.9231 (mock – replace with real geolocation)',
      quality: 'high',
      data: photoData,
    };

    setCaptured(photo);
    setIsReady(false);
    onCapture(photo);

    setTimeout(() => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }, 1500);
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsReady(false);
  };

  const handleButtonClick = () => {
    if (captured) {
      setCaptured(null);
      stopCamera();
      startCamera();
    } else if (isReady) {
      takePhoto();
    } else {
      startCamera();
    }
  };

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  return (
    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
      {captured ? (
        <div className="space-y-2">
          <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 h-48 rounded overflow-hidden">
            <img
              src={captured.data}
              alt="Captured photo"
              className="w-full h-full object-cover"
            />
            <div className="absolute top-2 right-2 bg-green-600 text-white text-xs px-2 py-1 rounded-full">
              ✓ Verified
            </div>
            <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
              {captured.location}
            </div>
          </div>
          <p className="text-xs text-center text-gray-600">
            {new Date(captured.timestamp).toLocaleString()}
          </p>
          <button
            onClick={handleButtonClick}
            className="w-full bg-red-600 hover:bg-red-700 text-white p-2 rounded transition-colors"
          >
            Take New Photo
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="bg-black rounded overflow-hidden h-48 flex items-center justify-center relative">
            {error ? (
              <p className="text-red-500 text-center p-2">{error}</p>
            ) : isStarting ? (
              <p className="text-white">Starting camera...</p>
            ) : (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
            )}
          </div>

          <canvas ref={canvasRef} className="hidden" />

          <button
            onClick={handleButtonClick}
            disabled={isStarting || !!error}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white p-3 rounded font-semibold transition-colors disabled:opacity-50"
          >
            <Camera className="inline mr-2" size={18} />
            {error ? 'Retry' : isStarting ? 'Starting...' : isReady ? 'Take Photo' : 'Start Camera'}
          </button>

          {error && (
            <button
              onClick={() => {
                setError(null);
                startCamera();
              }}
              className="w-full bg-yellow-600 text-white p-2 rounded mt-2"
            >
              Retry Camera
            </button>
          )}

          <p className="text-xs text-center text-gray-500">
            {label} {required && <span className="text-red-500">*</span>}
          </p>
        </div>
      )}
    </div>
  );
};

export default PhotoCapture;
